import React, { useState, useEffect } from 'react';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
    WifiIcon, 
    SignalIcon, 
    BellIcon, 
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ClockIcon
} from '@heroicons/react/24/outline';

function RealTimeTrackingDashboard() {
    const { user } = useAuth();
    const { connected, error, useSocketEvent, ping } = useSocket();
    const [pollingMode, setPollingMode] = useState(true);
    const [pollingInterval, setPollingInterval] = useState(null);
    const [realtimeStats, setRealtimeStats] = useState({
        activeTracking: 0,
        completedToday: 0,
        errorsToday: 0,
        lastUpdate: null
    });
    const [notifications, setNotifications] = useState([]);
    const [connectionQuality, setConnectionQuality] = useState('good');

    // Hibrit Polling sistemi (WebSocket + Fallback)
    useEffect(() => {
        // WebSocket bağlı değilse veya polling modu aktifse
        if (!connected || pollingMode) {
            const interval = setInterval(async () => {
                try {
                    // API'den güncel verileri çek (Authorization header ile)
                    const data = await api.get('/api/smart-tracking/status');
                    
                    if (data.success) {
                        setRealtimeStats(prev => ({
                            ...prev,
                            activeTracking: data.data.activePollsCount || 0,
                            lastUpdate: new Date()
                        }));
                        
                        // WebSocket bağlı değilse polling modunu aktif tut
                        if (!connected) {
                            setPollingMode(true);
                        }
                    }
                } catch (error) {
                    console.error('Polling hatası:', error);
                    // 401 hatası alırsak polling'i durdur
                    if (error.status === 401) {
                        setPollingMode(false);
                        toast.error('Kimlik doğrulama hatası - Lütfen tekrar giriş yapın');
                    }
                }
            }, connected ? 10000 : 5000); // WebSocket varsa 10sn, yoksa 5sn
            
            setPollingInterval(interval);
            
            return () => {
                if (interval) clearInterval(interval);
            };
        } else {
            // WebSocket bağlıysa polling'i durdur
            if (pollingInterval) {
                clearInterval(pollingInterval);
                setPollingInterval(null);
            }
        }
    }, [connected, pollingMode]);

    // REST fallback: /api/smart-tracking/notifications polling (3s)
    useEffect(() => {
        let t;
        const fetchNotifications = async () => {
            try {
                const res = await api.get('/api/smart-tracking/notifications?limit=20');
                if (res && res.success && Array.isArray(res.items)) {
                    // Yeni gelenler varsa state'i güncelle
                    setNotifications(res.items.map(it => ({
                        id: it.id,
                        type: it.type?.includes('error') ? 'error' : it.type?.includes('completed') ? 'success' : (it.type?.includes('started') ? 'success' : 'info'),
                        message: it.message || `${it.type} olayı`,
                        timestamp: new Date(it.timestamp || Date.now()),
                        data: it.data || null
                    })));
                    setRealtimeStats(prev => ({ ...prev, lastUpdate: new Date() }));
                }
            } catch (e) {
                // sessiz
            }
        };
        // websocket bağlı değilse veya pollingMode açıksa REST ile doldur
        if (!connected || pollingMode) {
            fetchNotifications();
            t = setInterval(fetchNotifications, 3000);
        }
        return () => t && clearInterval(t);
    }, [connected, pollingMode]);
    
    // Real-time event listeners
    useSocketEvent('tracking:started', (data) => {
        console.log('🚀 Real-time: Takip başladı:', data);
        toast.success(`📦 Sipariş ${data.orderId} takibi başlatıldı!`);
        
        setNotifications(prev => [{
            id: Date.now(),
            type: 'success',
            message: `Sipariş ${data.orderId} takibi başlatıldı`,
            timestamp: new Date(),
            data: data
        }, ...prev.slice(0, 9)]);
        
        setRealtimeStats(prev => ({
            ...prev,
            activeTracking: prev.activeTracking + 1,
            lastUpdate: new Date()
        }));
    });

    useSocketEvent('tracking:updated', (data) => {
        console.log('🔄 Real-time: Takip güncellendi:', data);
        
        setNotifications(prev => [{
            id: Date.now(),
            type: 'info',
            message: `Sipariş ${data.orderId} - ${data.phase} fazı güncellendi`,
            timestamp: new Date(),
            data: data
        }, ...prev.slice(0, 9)]);
        
        setRealtimeStats(prev => ({
            ...prev,
            lastUpdate: new Date()
        }));
    });

    useSocketEvent('tracking:completed', (data) => {
        console.log('✅ Real-time: Takip tamamlandı:', data);
        toast.success(`✅ Sipariş ${data.orderId} takibi tamamlandı!`);
        
        setNotifications(prev => [{
            id: Date.now(),
            type: 'success',
            message: `Sipariş ${data.orderId} takibi başarıyla tamamlandı`,
            timestamp: new Date(),
            data: data
        }, ...prev.slice(0, 9)]);
        
        setRealtimeStats(prev => ({
            ...prev,
            activeTracking: Math.max(0, prev.activeTracking - 1),
            completedToday: prev.completedToday + 1,
            lastUpdate: new Date()
        }));
    });

    useSocketEvent('tracking:error', (data) => {
        console.log('❌ Real-time: Takip hatası:', data);
        toast.error(`❌ Sipariş ${data.orderId} takip hatası: ${data.error}`);
        
        setNotifications(prev => [{
            id: Date.now(),
            type: 'error',
            message: `Sipariş ${data.orderId} hata: ${data.error}`,
            timestamp: new Date(),
            data: data
        }, ...prev.slice(0, 9)]);
        
        setRealtimeStats(prev => ({
            ...prev,
            errorsToday: prev.errorsToday + 1,
            lastUpdate: new Date()
        }));
    });

    useSocketEvent('service:status-changed', (data) => {
        console.log('🔧 Real-time: Servis durumu değişti:', data);
        toast.info(`🔧 Takip servisi ${data.status === 'started' ? 'başlatıldı' : 'durduruldu'}`);
        
        setNotifications(prev => [{
            id: Date.now(),
            type: 'info',
            message: `Takip servisi ${data.status === 'started' ? 'başlatıldı' : 'durduruldu'}`,
            timestamp: new Date(),
            data: data
        }, ...prev.slice(0, 9)]);
    });

    useSocketEvent('batch:update', (data) => {
        console.log('📊 Real-time: Batch güncelleme:', data);
        
        setRealtimeStats(prev => ({
            ...prev,
            lastUpdate: new Date()
        }));
    });

    // Bağlantı kalitesi kontrolü
    useEffect(() => {
        const interval = setInterval(() => {
            if (connected) {
                ping();
                setConnectionQuality('good');
            } else if (error) {
                setConnectionQuality('poor');
            } else {
                setConnectionQuality('connecting');
            }
        }, 30000); // 30 saniyede bir ping

        return () => clearInterval(interval);
    }, [connected, error, ping]);

    const getConnectionIcon = () => {
        switch (connectionQuality) {
            case 'good':
                return <WifiIcon className="w-5 h-5 text-green-500" />;
            case 'poor':
                return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
            default:
                return <SignalIcon className="w-5 h-5 text-yellow-500 animate-pulse" />;
        }
    };

    const getConnectionStatus = () => {
        if (connected) return { text: 'Bağlı', color: 'text-green-600' };
        if (error) return { text: 'Hata', color: 'text-red-600' };
        return { text: 'Bağlanıyor...', color: 'text-yellow-600' };
    };

    const formatTime = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleTimeString('tr-TR');
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
            case 'error':
                return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
            default:
                return <BellIcon className="w-5 h-5 text-blue-500" />;
        }
    };

    // Admin kontrolü - daha kapsamlı
    const isAdmin = user?.app_metadata?.roles?.includes('admin') || user?.role === 'admin';
    
    // GEÇİCİ: Test için admin bypass (daha sonra kaldırılacak)
    const isTestUser = user?.email === 'hikibo06@gmail.com';
    const finalAdminCheck = isAdmin || isTestUser;
    
    console.log('🔐 RealTimeTrackingDashboard Admin kontrolü:', { 
        user: user, 
        userRole: user?.role, 
        appMetadata: user?.app_metadata, 
        isAdmin,
        isTestUser,
        finalAdminCheck
    });
    
    if (!finalAdminCheck) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center space-x-3">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                    <div>
                        <h3 className="font-semibold text-red-800">Erişim Reddedildi</h3>
                        <p className="text-sm text-red-700">
                            Bu sayfa sadece admin kullanıcılar için erişilebilir.
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                            Mevcut rol: {user?.role || 'Tanımsız'} | Admin: {isAdmin ? 'Evet' : 'Hayır'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Hibrit Sistem Durumu */}
            <div className={`border rounded-xl p-4 ${
                connected 
                    ? 'bg-green-50 border-green-200' 
                    : pollingMode 
                        ? 'bg-yellow-50 border-yellow-200' 
                        : 'bg-red-50 border-red-200'
            }`}>
                <div className="flex items-center space-x-3">
                    {connected ? (
                        <WifiIcon className="w-6 h-6 text-green-600" />
                    ) : pollingMode ? (
                        <ClockIcon className="w-6 h-6 text-yellow-600" />
                    ) : (
                        <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                    )}
                    <div>
                        <h3 className={`font-semibold ${
                            connected ? 'text-green-800' : pollingMode ? 'text-yellow-800' : 'text-red-800'
                        }`}>
                            {connected ? '🚀 Hibrit Sistem Aktif' : pollingMode ? '⏱️ Polling Modu' : '❌ Bağlantı Yok'}
                        </h3>
                        <p className={`text-sm ${
                            connected ? 'text-green-700' : pollingMode ? 'text-yellow-700' : 'text-red-700'
                        }`}>
                            {connected 
                                ? 'WebSocket bağlı + Polling yedek sistemi çalışıyor. Anlık güncellemeler aktif.'
                                : pollingMode 
                                    ? 'WebSocket bağlanamadı, polling modu ile çalışıyor. 5 saniyede bir güncelleme.'
                                    : 'Bağlantı kurulamadı. Lütfen sayfayı yenileyin.'
                            }
                        </p>
                    </div>
                </div>
            </div>

            {/* Real-time Bağlantı Durumu */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        {getConnectionIcon()}
                        <div>
                            <h3 className="font-semibold text-gray-900">Real-time Bağlantı</h3>
                            <p className={`text-sm ${getConnectionStatus().color}`}>
                                {getConnectionStatus().text}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-600">Son Güncelleme</p>
                        <p className="text-sm font-medium text-gray-900">
                            {formatTime(realtimeStats.lastUpdate)}
                        </p>
                    </div>
                </div>
                
                {error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">Bağlantı Hatası: {error}</p>
                    </div>
                )}
            </div>

            {/* Real-time İstatistikler */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <ClockIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Aktif Takip</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {realtimeStats.activeTracking}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Bugün Tamamlanan</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {realtimeStats.completedToday}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Bugün Hata</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {realtimeStats.errorsToday}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Real-time Bildirimler */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BellIcon className="w-5 h-5 mr-2 text-gray-600" />
                    Canlı Bildirimler
                </h3>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">
                            Henüz bildirim yok. Real-time güncellemeler burada görünecek.
                        </p>
                    ) : (
                        notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                            >
                                {getNotificationIcon(notification.type)}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">
                                        {notification.message}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {formatTime(notification.timestamp)}
                                    </p>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default RealTimeTrackingDashboard;
